import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { cloneDeep, get, isEqual, isNil, zip } from 'lodash';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Footer } from 'src/components/footer/Footer';
import { PublicationModal, SimpleDialog } from 'src/components/modal';
import { Progress } from 'src/components/progress';
import { Snackbar } from 'src/components/snackbar';
import {
  IDaypartRecord,
  IScheduleWeekdayDaypartRecord,
  TimeRow,
} from 'src/components/timeRow';
import { getAssembliesStatus } from 'src/components/timeRow/TimeRow.helper';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants';
import { PATHS } from 'src/constants/paths';
import {
  scheduleAssembliesTransaction,
  scheduleDaypartsTransaction,
  useCreateOrUpdateScheduleDayparts,
  useGetScheduleDayparts,
  useGetWeekdayDayparts,
  useModal,
} from 'src/hooks';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { useGetDefaultAssemblyOfDayparts } from 'src/hooks/useGetDefaultAssemblyOfDayparts';
import { IAssembly, IDaypart } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';
import { locationGroupEditTimingsEvent } from 'src/utils/google-tag-manager';

import {
  buildScheduledAssembliesPayload,
  generateWeekdayDaypartRecords,
  getDefaultAssembly,
  getLoadingState,
  isSameAssembly,
  updateAssemblyInPlace,
  updateDaypartScheduleAssembly,
} from './LocationGroupTimingsGrid.helper';
import { LocationTimingsTimeGridProps } from './locationGroupTimingsGrid.interface';
import { Container } from './LocationTimingsTimeGrid.styled';

export const handleGetStartTime = (time: string | undefined | null) => {
  if (!time) return null;

  return dayjs(time, 'HH:mm:ss').format('HH:mm:00');
};

export const LocationTimingsTimeGrid = (
  props: LocationTimingsTimeGridProps,
) => {
  const { locationGroupId = '' } = useParams();
  const navigate = useNavigate();
  const saveModal = useModal();
  const publishModal = useModal();
  const [isSaving, setIsSaving] = useState(false);
  const [daypartsRecord, setDaypartsRecord] = useState<IDaypartRecord[]>();
  const [initialDaypartsRecord, setInitialDaypartsRecord] = useState<
    IDaypartRecord[]
  >([]);
  const [assemblyChanged, setAssemblyChanged] = useState<boolean>(false);
  const [
    originalScheduleWeekdayDaypartsRecord,
    setOriginalScheduleWeekdayDaypartsRecord,
  ] = useState<IScheduleWeekdayDaypartRecord[]>();
  const [scheduleWeekdayDaypartsRecord, setScheduleWeekdayDaypartsRecord] =
    useState<IScheduleWeekdayDaypartRecord[]>();
  const updateStoreTimes = useCreateOrUpdateScheduleDayparts();

  const { data: weekdayDaypartOptions, isLoading: isLoadingWeekdayDayparts } =
    useGetWeekdayDayparts({
      enabled: !props.rbStoreIntegration,
    });

  const enableScheduleDayparts = !props.rbStoreIntegration
    ? Boolean(locationGroupId)
    : !props.rbStoreIntegration;
  const { data: locGroupDayparts, isLoading: isLoadingLocGroupDayparts } =
    useGetScheduleDayparts(
      [`location_group_id=${locationGroupId}`],
      enableScheduleDayparts,
    );

  const { isViewOnly, isOwnerOperator } = useUserRole();
  const {
    daypartsWithDefaultAssembly,
    isLoadingDefaultAssemblies,
    isLoadingDayparts,
    errorLoadingDayparts,
    setDaypartsWithDefaultAssembly,
  } = useGetDefaultAssemblyOfDayparts(
    locationGroupId,
    props.rbStoreIntegration,
    undefined,
    setInitialDaypartsRecord,
  );

  const isLoading = getLoadingState(
    props.rbStoreIntegration,
    isLoadingDayparts,
    isLoadingWeekdayDayparts,
    isLoadingLocGroupDayparts,
    isLoadingDefaultAssemblies,
  );

  useEffect(() => {
    if (!props.rbStoreIntegration) {
      const daypartWeekdayTimes = generateWeekdayDaypartRecords({
        weekdayDaypartOptions,
        locGroupDayparts,
        locationGroupId: Number(locationGroupId),
      });

      if (daypartWeekdayTimes) {
        setOriginalScheduleWeekdayDaypartsRecord(
          cloneDeep(daypartWeekdayTimes),
        );
        setScheduleWeekdayDaypartsRecord(daypartWeekdayTimes);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekdayDaypartOptions, locGroupDayparts]);

  useEffect(() => {
    if (daypartsWithDefaultAssembly && !isLoading) {
      const daypartTimes = daypartsWithDefaultAssembly.map((daypart) => ({
        ...daypart,
        daypartActive: !props.rbStoreIntegration
          ? !!scheduleWeekdayDaypartsRecord
              ?.filter((weekday) => weekday.daypart.id === daypart.id)
              .some((item) => item.start_time)
          : !!daypart?.defaultAssembly,
      }));
      setDaypartsRecord(daypartTimes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    daypartsWithDefaultAssembly,
    props.rbStoreIntegration,
    originalScheduleWeekdayDaypartsRecord,
    isLoading,
  ]);

  useEffect(() => {
    setDaypartsRecord((prev) =>
      prev?.map((daypart) => {
        const hasStartTime = scheduleWeekdayDaypartsRecord
          ?.filter((weekday) => weekday.daypart.id === daypart.id)
          .some((item) => item.start_time);

        if (props.rbStoreIntegration) {
          return {
            ...daypart,
            daypartActive: !!daypart?.defaultAssembly,
          };
        }

        return {
          ...daypart,
          daypartActive: !!hasStartTime,
          ...(hasStartTime
            ? {
                defaultAssembly: daypart.defaultAssembly,
                schedule_assembly_id: daypart.schedule_assembly_id,
              }
            : {}),
        };
      }),
    );
  }, [props.rbStoreIntegration, scheduleWeekdayDaypartsRecord]);

  const onChangeAssembly = (daypart_id: number, assembly: IAssembly) => {
    updateDaypartScheduleAssembly(
      setDaypartsWithDefaultAssembly,
      daypart_id,
      (current) => {
        !assemblyChanged && setAssemblyChanged(true);

        return updateAssemblyInPlace(current, assembly);
      },
    );
  };

  const onHandleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      if (
        !props.rbStoreIntegration &&
        daypartsRecord?.some(
          (daypart) =>
            daypart.daypartActive &&
            !getAssembliesStatus(daypart.defaultAssembly).defaultAssembly,
        )
      ) {
        Snackbar.error('All enabled dayparts must have a default assembly');
      } else {
        let payload:
          | scheduleDaypartsTransaction[]
          | scheduleAssembliesTransaction[] = [];
        if (!props.rbStoreIntegration) {
          let dayparts: IDaypart[] = [];
          let location_group_id: number | undefined;
          payload = zip(
            originalScheduleWeekdayDaypartsRecord ?? [],
            scheduleWeekdayDaypartsRecord ?? [],
          )
            ?.filter(
              ([originalScheduleDaypart, scheduleDaypart]) =>
                originalScheduleDaypart?.start_time !==
                scheduleDaypart?.start_time,
            )
            .map(
              ([_, scheduleDaypart]) =>
                scheduleDaypart as IScheduleWeekdayDaypartRecord,
            )
            .map((scheduleDaypart) => {
              dayparts.push(get(scheduleDaypart, 'daypart'));
              location_group_id = get(
                scheduleDaypart,
                'location_group_id',
                location_group_id,
              );
              const startTime = handleGetStartTime(
                get(scheduleDaypart, 'start_time'),
              );

              return {
                resource: COLLECTIONS.SCHEDULE_DAYPARTS,
                method: scheduleDaypart?.id ? 'PATCH' : 'POST',
                id: get(scheduleDaypart, 'id'),
                data: scheduleDaypart?.id
                  ? {
                      start_time: startTime,
                      narrative: narrativeGenerators[
                        COLLECTIONS.SCHEDULE_DAYPARTS
                      ][USER_ACTIONS.UPDATE](
                        scheduleDaypart?.daypart?.name,
                        scheduleDaypart?.weekday?.name,
                        props.locationGroupData?.name,
                      ),
                    }
                  : {
                      location_group_id,
                      weekday_daypart_id: get(
                        scheduleDaypart,
                        'weekday_daypart_id',
                      ),
                      start_time: startTime,
                      narrative: narrativeGenerators[
                        COLLECTIONS.SCHEDULE_DAYPARTS
                      ][USER_ACTIONS.CREATE](
                        scheduleDaypart?.daypart?.name,
                        scheduleDaypart?.weekday?.name,
                        props.locationGroupData?.name,
                      ),
                    },
              };
            });
          if (!isNil(location_group_id)) {
            locationGroupEditTimingsEvent(
              location_group_id,
              dayparts,
              get(props, 'locationGroupData.channel_name'),
            );
          }
        }

        if (assemblyChanged) {
          const scheduledAssembliesPayload = buildScheduledAssembliesPayload(
            daypartsRecord,
            initialDaypartsRecord,
            Number(locationGroupId),
            props.locationGroupData?.name,
          );
          payload = [...payload, ...scheduledAssembliesPayload];
        }

        await updateStoreTimes.mutateAsync({
          payload,
          locationGroupId,
        });
        setAssemblyChanged(false);
        saveModal.onCloseModal();
        setInitialDaypartsRecord(daypartsRecord ?? []);
        if (props.locationsCount > 0) {
          publishModal.onOpenModal();
        } else {
          Snackbar.success(`${lang.publicationModal.title}`);
        }
      }
    } catch {
      Snackbar.error(lang.failedSaveText);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangeScheduleDayparts = (
    scheduleDayparts: IScheduleWeekdayDaypartRecord[],
  ) => {
    const updatedMap = new Map(
      scheduleDayparts.map((s) => [s.weekday_daypart_id, s]),
    );

    setScheduleWeekdayDaypartsRecord((prev) => {
      if (!prev) return prev;

      return prev.map(
        (item) => updatedMap.get(item.weekday_daypart_id) || item,
      );
    });
  };

  const anyAssemblyOrDaypartUpdates =
    assemblyChanged ||
    !isEqual(
      originalScheduleWeekdayDaypartsRecord,
      scheduleWeekdayDaypartsRecord,
    );

  const removeScheduleAssemblyRecords = (daypartId: number) => {
    updateDaypartScheduleAssembly(
      setDaypartsWithDefaultAssembly,
      daypartId,
      (current) => {
        if (current.length <= 1) return current;

        const defaultAssemblyIndex = getDefaultAssembly(current);

        if (defaultAssemblyIndex === -1) return current;

        !assemblyChanged && setAssemblyChanged(true);

        return [current[defaultAssemblyIndex]];
      },
    );
  };

  const updatedDaypartRecords = (
    daypartId: number,
    assembly: IAssembly,
    startDate?: string,
  ) => {
    updateDaypartScheduleAssembly(
      setDaypartsWithDefaultAssembly,
      daypartId,
      (current) => {
        const newAssembly = { ...assembly, startDate };

        // ignore if already present
        if (current.some((a) => isSameAssembly(a, newAssembly))) {
          return current;
        }

        // ignore if max limit reached
        if (current.length >= 2) {
          return current;
        }
        !assemblyChanged && setAssemblyChanged(true);

        return [...current, newAssembly];
      },
    );
  };

  return (
    <Container>
      {isLoading ? (
        <Progress />
      ) : errorLoadingDayparts ? (
        <Typography>{lang.errorOccurred}</Typography>
      ) : (
        <>
          {daypartsRecord?.map((dayPart, index) => {
            const scheduleDayparts = scheduleWeekdayDaypartsRecord?.filter(
              (weekday) => weekday?.daypart?.id === dayPart.id,
            );
            const originalScheduleDayparts =
              originalScheduleWeekdayDaypartsRecord?.filter(
                (weekday) => weekday?.daypart?.id === dayPart.id,
              );

            return (
              <TimeRow
                key={index}
                locationGroup={props.locationGroupData}
                daypartRecord={dayPart}
                originalScheduledDayparts={originalScheduleDayparts}
                scheduledDayparts={scheduleDayparts}
                scheduleWeekdayDaypartsRecord={scheduleWeekdayDaypartsRecord}
                setDefaultAssembly={onChangeAssembly}
                rbStoreIntegration={props.rbStoreIntegration}
                isLocationGroupPage={true}
                handleChangeScheduleDayparts={handleChangeScheduleDayparts}
                options={{
                  canAddAssembly: !isViewOnly && !isOwnerOperator,
                  canEditAssembly: !isViewOnly && !isOwnerOperator,
                  canScheduleAssembly: !isViewOnly && !isOwnerOperator,
                }}
                setScheduleAssembly={updatedDaypartRecords}
                removeScheduleAssembly={removeScheduleAssemblyRecords}
              />
            );
          })}
        </>
      )}

      {saveModal.open && (
        <SimpleDialog
          open={saveModal.open}
          cancelText={lang.cancel}
          confirmText={lang.confirm}
          title={lang.titleTextSave}
          subTitle={lang.subTitlePublishConfirm}
          onClose={saveModal.onCloseModal}
          onClickCancel={saveModal.onCloseModal}
          modalSize="sm"
          disabled={isSaving}
          onClickConfirm={onHandleSave}
          isLoading={updateStoreTimes.isLoading}
        />
      )}
      {publishModal.open && (
        <PublicationModal
          open={publishModal.open}
          locationGroupIDs={[Number(locationGroupId)]}
          onClose={publishModal.onCloseModal}
        />
      )}
      {!isViewOnly && (
        <Footer
          showFooterActions
          hasSubNav
          onCancel={() => navigate(PATHS.LOCATION_GROUPS)}
          onSave={saveModal.onOpenModal}
          disableSave={!anyAssemblyOrDaypartUpdates}
        />
      )}
    </Container>
  );
};
